import React, { useEffect, useState } from "react";
import {
  Form,
  Select,
  InputNumber,
  DatePicker,
  Button,
  Table,
  Space,
  Grid,
  Card,
} from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { fetchLoadIntoAccs, fetchLoadIntoAccsExcludeCheque } from "../../store/commonThunks/lookups";
import moment from "moment";
import { useWatch } from "antd/es/form/Form";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import InputField from "../Input/Input";

const { Summary } = Table;
const { useBreakpoint } = Grid;

const ChequePayment = ({
  onFooterChange,
  conversionRate,
  defaultAccount,
  pageType,
  footerValues,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const screens = useBreakpoint();
  const isMobile = !screens.md || !screens.xl; // Adjust the breakpoint as per requirement

  const lookups = useSelector((s) => s.lookups);

  const [data, setData] = useState([]);
  const [editingKey, setEditingKey] = useState("");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const type = useWatch("type", form);
  const isMoneyOut = pageType === "salesReturn" || pageType === "purchaseEntry" || pageType === "purchase";
  

  useEffect(() => {
    dispatch(fetchLoadIntoAccs(""));
    dispatch(fetchLoadIntoAccsExcludeCheque());
  }, [dispatch]);

  useEffect(() => {
    if (footerValues?.payCart && Array.isArray(footerValues.payCart)) {
      // Filter payments that should appear in ChequePayment
      // Assuming ChequePayment shows all payments from payCart
      const formattedData = footerValues.payCart.map((item, idx) => ({
        key: item.key || `payment_${idx}`,
        type: item.type || item.to_acc_name,
        acc_type_id: item.acc_type_id,
        tran_amount: parseFloat(item.tran_amount) || 0,
          cheque_number: item.cheque_number || "",
        date: item.date ? dayjs(item.date) : null,
        from_acc_id: item.from_acc_id,
        from_acc_name: item.from_acc_name || item.type,
        expected_bank_acc_id: item.expected_bank_acc_id || null,
      }));
      setData(formattedData);
    }
  }, [footerValues?.payCart]);


  const columns = [
    {
      title: isMoneyOut
        ? "Payment From Account"
        : "Received Into Account",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Amount",
      dataIndex: "tran_amount",
      key: "tran_amount",
      render: (val) => {
        const amount = Number(val);
        const finalAmount = conversionRate ? amount * conversionRate : amount;
        return amount ? finalAmount?.toFixed(2) : "0.00";
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (val) => val ? dayjs(val).format("DD-MM-YYYY") : ""
    },
    {
      title: "Cheque Number",
      dataIndex: "cheque_number",
      key: "cheque_number",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button icon={<EditOutlined />} onClick={() => edit(record)} />
          <Button
            icon={<DeleteOutlined />}
            onClick={() => remove(record.type)}
          />
        </Space>
      ),
    },
  ];

  const onFinish = (values) => {
    const newData = {
      key: values.type,
      type: values.type,
      acc_type_id: selectedAccount?.acc_type_id,
      cheque_number: values.cheque_number,
      tran_amount: values.amount,
      date: values.date
        ? values.date.format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD"),
      from_acc_id: selectedAccount?.acc_id || null,
      from_acc_name: selectedAccount?.acc_name || values.type,
      expected_bank_acc_id: values.expected_bank_acc_id || null,
    };

    const index = data.findIndex((item) => item.type === values.type);

    if (index > -1) {
      const newDataList = [...data];
      newDataList[index] = newData;

      onFooterChange(newDataList, "payCart");
      setData(newDataList);
    } else {
      onFooterChange([...data, newData], "payCart");
      setData([...data, newData]);
    }
    form.resetFields();
    setEditingKey("");
  };

  const edit = (record) => {
    const matchedAccount = lookups.loadIntoAccs.find(
      (a) =>
        a.acc_name === record.from_acc_name ||
        a.acc_type_id === record.from_acc_type_id
    );

    // Update form fields
    form.setFieldsValue({
      type: matchedAccount?.acc_name || record.from_acc_name,
      amount: record.tran_amount,
      // date: record.date ? moment(record.date, "YYYY-MM-DD") : null,
      date: record.date ? dayjs(record.date) : null,
      expected_bank_acc_id: record.expected_bank_acc_id || null,
    });

    // Store the full selected object in state
    setSelectedAccount(matchedAccount || null);

    // (optional) mark editingKey if you use it for UI state
    setEditingKey(record.type);
  };

  const remove = (type) => {
    // setData(data.filter((item) => item.type !== type));

    const newDataList = data.filter((item) => item.key !== type);
    setData(newDataList);
    onFooterChange(newDataList, "payCart");
  };

  return (
    <>
      <Card title="Payment" style={{ marginBottom: 5 }}>
        <Form
          form={form}
          onFinish={onFinish}
          layout={!isMobile ? "inline" : "horizontal"}
          // variant="filled"
          style={{
            width: "100%",
            gap: 10,
          }}
        >
          <Form.Item
            name="type"
            style={{
              flexGrow: 1,
              marginBottom: "10px",
            }}
            rules={[{ required: true, message: "Please select a type!" }]}
          >
            <Select
              style={{
                minWidth: 150,
              }}
              data-testid="received-into-account"
              placeholder={
                pageType === "salesReturn" || pageType === "purchaseEntry"
                  ? "Payment From Account"
                  : "Received Into Account"
              }
              options={lookups?.loadIntoAccs?.map((accs) => ({
                label: accs.acc_name,
                value: accs.acc_name,
                obj: accs,
              }))}
              onChange={(value, option) => {
                setSelectedAccount(option.obj);
                // You can also set hidden form fields if needed
                form.setFieldValue("selectedAccId", option.obj.acc_id);
              }}
              disabled={!!editingKey}
            />
          </Form.Item>

          <Form.Item
            name="amount"
            style={{
              flexGrow: 1,
              marginBottom: "10px",
            }}
            rules={[{ required: true, message: "Please input the amount!" }]}
          >
            <InputNumber
              min={0}
              style={{ width: "100%" }}
              placeholder="Amount"
              formatter={(value) => {
                parseFloat(
                  conversionRate ? value * conversionRate : value
                )?.toFixed(2);
              }}
              data-testid="amount-input"
              onChange={(value) => {
                // Apply conversion instantly
                const amount = conversionRate ? value / conversionRate : value;
                form.setFieldsValue({ amount: Number(amount).toFixed(2) });
              }}
              onWheel={(e) => e.target.blur()}
            />
          </Form.Item>

          {selectedAccount?.acc_type_id == "cheque" && (
            <Form.Item
              name="cheque_number"
              style={{
                marginBottom: "10px",
                minWidth: 150,
              }}
            >
              <InputField placeholder="Enter Cheque Number" />
            </Form.Item>
          )}

          {selectedAccount?.acc_type_id == "cheque" && (
            <Form.Item
              name="expected_bank_acc_id"
              style={{
                marginBottom: "10px",
                minWidth: 150,
              }}
            >
              <Select
                allowClear
                placeholder="Expected Bank (optional)"
                options={(lookups?.loadIntoAccounts || []).map((accs) => ({
                  label: accs.acc_name,
                  value: accs.acc_id,
                }))}
              />
            </Form.Item>
          )}

          {selectedAccount?.acc_name == "PDC" && (
            <Form.Item
              name="date"
              style={{
                marginBottom: "10px",
                minWidth: 150,
              }}
              rules={[{ required: true, message: "Please select the date!" }]}
            >
              <DatePicker
                style={{
                  width: "100%",
                }}
                disabledDate={(current) => {
                  return current && current <= dayjs().endOf("day");
                }}
              />
            </Form.Item>
          )}

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              // data-testid="cheque-add-payment-btn"
              data-testid="add-payment-btn"
            >
              {editingKey ? "Update" : "Add"}
            </Button>
          </Form.Item>
        </Form>
        <Table
          columns={columns}
          dataSource={data}
          scroll={{ x: "max-content" }}
          style={{ marginTop: 20, width: "100%" }}
          summary={() => (
            <Summary.Row>
              <Summary.Cell
                index={0}
                colSpan={3}
                className="table-footer-title"
              >
                Total Paid:
              </Summary.Cell>
              <Summary.Cell colSpan={1} index={2} className="table-footer-data">
                {/* {data
                  ?.reduce((sum, item) => sum + parseFloat(item.tran_amount), 0)
                  ?.toFixed(2)} */}

                <strong>
                  {(conversionRate
                    ? data.reduce(
                        (sum, item) =>
                          sum + Number(item.tran_amount || 0) * conversionRate,
                        0
                      )
                    : data.reduce(
                        (sum, item) => sum + Number(item.tran_amount || 0),
                        0
                      )       
                  ).toFixed(2)}
                </strong>
              </Summary.Cell>
            </Summary.Row>
          )}
        />
      </Card>
    </>
  );
};

export default ChequePayment;
